import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./Navbar.css";

// ✅ change this path/file name to your real logo file
import logo from "../../assets/logo.png";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("hero");

  const navigate = useNavigate();
  const location = useLocation();

  const phoneTel = "+94776500580";
  const isHome = location.pathname === "/";
  const isProductsPage = location.pathname === "/products";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Observe sections only on Home page
  useEffect(() => {
    if (!isHome) return;

    const ids = ["hero", "about", "contact"]; // products is a route now
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: [0.12, 0.25, 0.4] }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [isHome]);

  const scrollToId = (id) => {
    const el = document.getElementById(id);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const goSection = (id) => {
    setOpen(false);

    if (isHome) return scrollToId(id);

    // If currently on /products, go home then scroll when DOM is ready
    navigate("/");
    let tries = 0;
    const tick = () => {
      tries += 1;
      const el = document.getElementById(id);
      if (el) return scrollToId(id);
      if (tries < 60) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  const goProducts = () => {
    setOpen(false);
    navigate("/products");
  };

  return (
    <header className={`ntcNavWrap ${scrolled ? "isScrolled" : ""}`}>
      <div className="ntcNavInner">
        {/* Brand */}
        <a
          className="ntcBrand"
          href="#Hero"
          onClick={(e) => {
            e.preventDefault();
            goSection("hero");
          }}
        >
          <img className="ntcLogo" src={logo} alt="New Alubomulla Trade Center logo" />
          <div className="ntcBrandText">
            <b>New Alubomulla</b>
            <span>Trade Center</span>
          </div>
        </a>

        {/* Desktop links */}
        <nav className="ntcLinks" aria-label="Main navigation">
          <button
            className={`ntcLink ${isHome && active === "hero" ? "isActive" : ""}`}
            onClick={() => goSection("hero")}
          >
            Home
          </button>

          <button
            className={`ntcLink ${isProductsPage ? "isActive" : ""}`}
            onClick={goProducts}
          >
            Products
          </button>

          <button
            className={`ntcLink ${isHome && active === "about" ? "isActive" : ""}`}
            onClick={() => goSection("about")}
          >
            About
          </button>

          <button
            className={`ntcLink ${isHome && active === "contact" ? "isActive" : ""}`}
            onClick={() => goSection("contact")}
          >
            Contact
          </button>
        </nav>

        {/* Right */}
        <div className="ntcRight">
          <a className="ntcCall" href={`tel:${phoneTel}`}>
            Call Now
          </a>

          <button
            className={`ntcBurger ${open ? "isOpen" : ""}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Open menu"
            aria-expanded={open}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>

      {/* Mobile dropdown */}
      <div className={`ntcMobile ${open ? "open" : ""}`}>
        <button
          className={`ntcMItem ${isHome && active === "hero" ? "isActive" : ""}`}
          onClick={() => goSection("hero")}
        >
          Home
        </button>

        <button
          className={`ntcMItem ${isProductsPage ? "isActive" : ""}`}
          onClick={goProducts}
        >
          Products
        </button>

        <button
          className={`ntcMItem ${isHome && active === "about" ? "isActive" : ""}`}
          onClick={() => goSection("about")}
        >
          About
        </button>

        <button
          className={`ntcMItem ${isHome && active === "contact" ? "isActive" : ""}`}
          onClick={() => goSection("contact")}
        >
          Contact
        </button>

        <a className="ntcMCall" href={`tel:${phoneTel}`}>
          Call Now
        </a>
      </div>
    </header>
  );
}
